/**
 * DataTable — единая переиспользуемая таблица результатов (P1.3, 2026-08-14).
 *
 * Построена на @tanstack/react-table. Заменяет разрозненные grid'ы
 * (SearchDataGrid / GenerationDataGrid / matching ResultsTable / Files
 * display-таблицы) одним компонентом. Cell-рендер остаётся в column-def'ах
 * каждого модуля — DataTable только каркас (pagination/pin/sort/filter/scroll).
 *
 * Возможности (ТЗ SearchV2 Phase 1 §5; ТЗ matching v2 §C.6 — 2026-09-23):
 *  - Пагинация 10 / 25 / 50 / 100 / All (справа).
 *  - Закрепление ЛЮБОГО столбца СЛЕВА или СПРАВА (ограничение «≤3» снято):
 *    pin-кнопка в заголовке — переключатель «нет пина → слева → справа → нет»,
 *    порядок закрепления = порядок кликов. Зона закрепления ограничена
 *    MAX_PIN_ZONE_RATIO (60%) ширины контейнера — лишние закрепления
 *    не применяются, но состояние пина сохраняется (видимая подсказка под
 *    тулбаром). `pinLeftCount` / `pinRightCount` / `initialPinnedColumns[Right]`
 *    — стартовые наборы (не предел).
 *  - Виртуализация строк (@tanstack/react-virtual) — ОПЦИОНАЛЬНО, по умолчанию
 *    выключена (`virtualizeRows`); при включении работает от VIRTUALIZE_MIN_ROWS
 *    строк на странице и рендерит только видимое окно.
 *  - Сортировка А-Я / Я-А по клику на заголовок (asc → desc → clear).
 *  - Глобальный поиск + faceted-фильтр по колонке (уникальные значения;
 *    для больших датасетов — `loadUniqueValues` lazy-порциями, иначе
 *    выводятся уникальные значения из `data`, cap=1000).
 *  - Горизонтальный скролл с hover-зонами 120px слева/справа + автопрокрутка
 *    (скорость пропорциональна глубине проникновения в зону).
 *  - onRowClick — клик по строке (открытие detail drawer остаётся за модулем).
 *
 * Границы: transform-движок Files НЕ затрагивается — только display-таблицы.
 * UX-J2: бренды провайдеров не утекают — cell-рендеры отвечает модуль.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  flexRender,
  type ColumnDef,
  type Column,
  type Row,
  type SortingState,
  type ColumnPinningState,
  type PaginationState,
  type Updater,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  X,
  Pin,
} from 'lucide-react';

import { cn } from '@/lib/utils';

const DEFAULT_PAGE_SIZES: (number | 'all')[] = [10, 25, 50, 100, 'all'];
const MAX_CLIENT_UNIQUE = 1000;
const LAZY_CHUNK = 100;
/** Максимальная доля ширины контейнера под закреплённую слева зону (§C.6). */
const MAX_PIN_ZONE_RATIO = 0.6;
/** Виртуализация строк включается только от такого числа строк на странице. */
const VIRTUALIZE_MIN_ROWS = 50;
/** Оценка высоты строки для виртуализатора (уточняется measureElement). */
const ROW_ESTIMATE_PX = 44;
/** Дефолтная ширина колонки, если column-def не задал `size`. */
const DEFAULT_COL_SIZE = 150;

export interface DataTableProps<TData> {
  columns: ColumnDef<TData>[];
  data: TData[];
  /** Клик по строке (опц.). */
  onRowClick?: (row: TData) => void;
  /** Стартовый набор закреплённых слева колонок — первые N (не предел:
   *  дальше пользователь закрепляет любой столбец pin-кнопкой в заголовке;
   *  порядок закрепления = порядок кликов). Игнорируется, если задан
   *  `initialPinnedColumns`. */
  pinLeftCount?: number;
  /** Стартовый набор закреплённых слева колонок по id, независимо от их
   *  порядка в `columns` (§C.6: «по умолчанию название товара»). Порядок
   *  массива = порядок закрепления. Если передан — заменяет `pinLeftCount`.
   *  Несуществующие сейчас id не отбрасываются: колонка появится позже —
   *  пин применится сам (в лимит ширины такие id не идут). */
  initialPinnedColumns?: string[];
  pinRightCount?: number;
  initialPinnedColumnsRight?: string[];
  /** Показывать ли pin-кнопку в заголовках колонок (по умолчанию — да). */
  enablePinning?: boolean;
  /** Виртуализация строк (@tanstack/react-virtual) — опционально, по умолчанию
   *  ВЫКЛЮЧЕНА: при выключенной разметка/высоты/скролл ровно как до этой
   *  правки, даже на тысячах строк. Включать точечно там, где это нужно
   *  (§C.6 — таблица результатов сопоставления). Дополнительно требует
   *  ≥ `VIRTUALIZE_MIN_ROWS` строк на странице и отсутствия `renderAuxRow`. */
  virtualizeRows?: boolean;
  /** Опции размера страницы. */
  pageSizeOptions?: (number | 'all')[];
  /** Загрузка уникальных значений колонки порциями (для больших датасетов).
   *  Если не задано — уникальные значения выводятся из data (cap 1000). */
  loadUniqueValues?: (
    columnId: string,
    offset: number,
    limit: number,
  ) => Promise<{ values: string[]; total: number }>;
  /** Пустое состояние (кастомный текст/нода). */
  emptyState?: React.ReactNode;
  /** loading overlay (когда data ещё грузится). */
  isLoading?: boolean;
  /** Дополнительный className для scroll-контейнера. */
  className?: string;
  /** Доп. className для <tr> по строке (напр. подсветка активной строки). */
  rowClassName?: (row: TData) => string;
  /** Доп. <tr>-строки после основной строки данных (banner-строки с colSpan,
   *  expand-детали и т.п.). Возвращает ноду из одного или нескольких <tr>. */
  renderAuxRow?: (row: TData, index: number) => React.ReactNode;
}

export function DataTable<TData>({
  columns,
  data,
  onRowClick,
  pinLeftCount = 0,
  initialPinnedColumns,
  pinRightCount = 0,
  initialPinnedColumnsRight,
  enablePinning = true,
  virtualizeRows = false,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  loadUniqueValues,
  emptyState,
  isLoading = false,
  className,
  rowClassName,
  renderAuxRow,
}: DataTableProps<TData>) {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [pageSizeRaw, setPageSizeRaw] = useState<number | 'all'>(10);
  // «Состояние пина»: желаемый набор закреплённых слева И СПРАВА колонок
  // (в порядке закрепления). Переживает сортировку/фильтр/смену страницы и
  // перерисовку консьюмера. Фактически применяются их префиксы
  // (`effectivePinnedLeft` / `effectivePinnedRight`), укладывающиеся в
  // MAX_PIN_ZONE_RATIO ширины контейнера с каждой стороны.
  const [pinnedLeft, setPinnedLeft] = useState<string[]>([]);
  const [pinnedRight, setPinnedRight] = useState<string[]>([]);
  const [colWidths, setColWidths] = useState<Record<string, number>>({});
  const [containerWidth, setContainerWidth] = useState(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);

  // Стартовый набор пинов: `initialPinnedColumns` (по id, независимо от порядка
  // колонок — §C.6 «по умолчанию название товара») либо первые N колонок из
  // `pinLeftCount` (кламп ≤3 снят). Применяется только когда сам набор
  // изменился — иначе ручные пины сбивались бы каждым рендером консьюмера с
  // новой identity `columns`. Идемпотентно.
  const seedPins = useMemo(() => {
    if (initialPinnedColumns) {
      // Дедуп с сохранением порядка: порядок массива = порядок закрепления.
      return Array.from(new Set(initialPinnedColumns));
    }
    return columns
      .slice(0, Math.max(0, pinLeftCount))
      .map(columnIdOf)
      .filter((id): id is string => Boolean(id));
  }, [columns, pinLeftCount, initialPinnedColumns]);
  const seedKey = seedPins.join('\u0000');
  const appliedSeedRef = useRef<string | null>(null);
  useEffect(() => {
    if (appliedSeedRef.current === seedKey) return;
    appliedSeedRef.current = seedKey;
    setPinnedLeft((prev) => (sameIds(prev, seedPins) ? prev : seedPins));
  }, [seedKey, seedPins]);

  // Правый пин: `initialPinnedColumnsRight` либо последние N колонок.
  // Идемпотентно, как и левый сид (см. выше).
  const seedPinsRight = useMemo(() => {
    if (initialPinnedColumnsRight) {
      // Дедуп с сохранением порядка: порядок массива = порядок закрепления.
      return Array.from(new Set(initialPinnedColumnsRight));
    }
    return columns
      .slice(Math.max(0, columns.length - Math.max(0, pinRightCount)), columns.length)
      .map(columnIdOf)
      .filter((id): id is string => Boolean(id));
  }, [columns, pinRightCount, initialPinnedColumnsRight]);
  const seedKeyRight = seedPinsRight.join('\u0000');
  const appliedSeedRefRight = useRef<string | null>(null);
  useEffect(() => {
    if (appliedSeedRefRight.current === seedKeyRight) return;
    appliedSeedRefRight.current = seedKeyRight;
    setPinnedRight((prev) => (sameIds(prev, seedPinsRight) ? prev : seedPinsRight));
  }, [seedKeyRight, seedPinsRight]);

  // Sync pageSizeRaw → pagination (All expands to full data length).
  useEffect(() => {
    if (pageSizeRaw === 'all') {
      setPagination(() => ({ pageIndex: 0, pageSize: data.length || 1 }));
    } else {
      setPagination(() => ({ pageIndex: 0, pageSize: pageSizeRaw }));
    }
  }, [pageSizeRaw, data.length]);

  // Обмер реальных ширин колонок и ширины контейнера. Нужен дважды: (1) offset
  // sticky-ячеек — `getSize()` при `table-layout: auto` лишь «подсказка», и на
  // нескольких пинах накопленная ошибка даёт наложение; (2) лимит закреплённой
  // зоны в 60% ширины контейнера. Идемпотентно: setState только при реальном
  // изменении, поэтому ResizeObserver не уходит в цикл рендеров.
  useEffect(() => {
    const scroller = scrollRef.current;
    const tbl = tableRef.current;
    if (!scroller || !tbl) return;
    let raf = 0;
    const headerCells = () =>
      Array.from(
        tbl.querySelectorAll<HTMLTableCellElement>('thead tr:last-child th[data-col-id]'),
      );
    const read = () => {
      raf = 0;
      const cw = scroller.clientWidth;
      setContainerWidth((prev) => (prev === cw ? prev : cw));
      const next: Record<string, number> = {};
      for (const th of headerCells()) {
        const id = th.getAttribute('data-col-id');
        if (id) next[id] = th.offsetWidth;
      }
      setColWidths((prev) => (sameWidths(prev, next) ? prev : next));
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(read);
    };
    const ro = new ResizeObserver(schedule);
    ro.observe(scroller);
    ro.observe(tbl);
    // Ширины колонок меняются и без изменения габаритов самой таблицы
    // (`w-full` + table-layout: auto перераспределяет место) — поэтому
    // наблюдаем ещё и каждую ячейку заголовка.
    for (const th of headerCells()) ro.observe(th);
    schedule();
    return () => {
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [columns]);

  // Заявленные в column-def ширины — fallback до первого обмера DOM.
  const declaredSizes = useMemo(() => {
    const map: Record<string, number> = {};
    for (const c of columns) {
      const id = columnIdOf(c);
      if (id) map[id] = typeof c.size === 'number' ? c.size : DEFAULT_COL_SIZE;
    }
    return map;
  }, [columns]);

  const widthOf = useCallback(
    (id: string) => colWidths[id] ?? declaredSizes[id] ?? DEFAULT_COL_SIZE,
    [colWidths, declaredSizes],
  );

  // Пин может «повиснуть» на колонке, которой больше нет в таблице (динамические
  // property-колонки matching): такие id не должны занимать лимит ширины зоны.
  const livePinnedLeft = useMemo(
    () => pinnedLeft.filter((id) => id in declaredSizes),
    [pinnedLeft, declaredSizes],
  );
  const livePinnedRight = useMemo(
    () => pinnedRight.filter((id) => id in declaredSizes),
    [pinnedRight, declaredSizes],
  );

  const { applied: effectivePinnedLeft, skipped: skippedPinnedCount } = useMemo(
    () => fitPinnedToWidth(livePinnedLeft, widthOf, containerWidth),
    [livePinnedLeft, widthOf, containerWidth],
  );
  const { applied: effectivePinnedRight, skipped: skippedPinnedCountRight } = useMemo(
    () => fitPinnedToWidthRight(livePinnedRight, widthOf, containerWidth),
    [livePinnedRight, widthOf, containerWidth],
  );

  const columnPinning = useMemo<ColumnPinningState>(
    () => ({ left: effectivePinnedLeft, right: effectivePinnedRight }),
    [effectivePinnedLeft, effectivePinnedRight],
  );

  // Pin-кнопка — переключатель трёх состояний: нет пина → слева → справа →
  // нет («none → left → right → none»). По умолчанию столбец закрепляется
  // слева; повторный клик переносит его вправо; третий — снимает пин.
  const togglePin = useCallback(
    (columnId: string) => {
      const wasLeft = pinnedLeft.includes(columnId);
      const wasRight = pinnedRight.includes(columnId);
      if (wasLeft) {
        // слева → справа
        setPinnedLeft((prev) => prev.filter((id) => id !== columnId));
        setPinnedRight((prev) => (prev.includes(columnId) ? prev : [...prev, columnId]));
      } else if (wasRight) {
        // справа → нет пина
        setPinnedRight((prev) => prev.filter((id) => id !== columnId));
      } else {
        // нет пина → слева
        setPinnedLeft((prev) => (prev.includes(columnId) ? prev : [...prev, columnId]));
      }
    },
    [pinnedLeft, pinnedRight],
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter, columnPinning, pagination },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    // Пишем в «состояние пина» (желаемый набор), а не в применённый префикс:
    // иначе обрезка по ширине затирала бы закрепления пользователя.
    onColumnPinningChange: (updater: Updater<ColumnPinningState>) => {
      const nextState =
        typeof updater === 'function'
          ? updater({ left: pinnedLeft, right: pinnedRight })
          : updater;
      const nextLeft = nextState.left ?? [];
      const nextRight = nextState.right ?? [];
      setPinnedLeft((prev) => (sameIds(prev, nextLeft) ? prev : nextLeft));
      setPinnedRight((prev) => (sameIds(prev, nextRight) ? prev : nextRight));
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: 'includesString',
    autoResetPageIndex: true,
  });

  // === Horizontal auto-scroll: 120px hover zones left/right ===
  useEdgeAutoScroll(scrollRef);

  const pageRows = table.getRowModel().rows;
  const totalRows = table.getFilteredRowModel().rows.length;
  const pageCount = table.getPageCount();
  const pageIndex = pagination.pageIndex;
  const canPrev = table.getCanPreviousPage();
  const canNext = table.getCanNextPage();

  // === Виртуализация строк (@tanstack/react-virtual) ===
  // Opt-in (`virtualizeRows`), по умолчанию выключена: остальные потребители
  // DataTable сохраняют прежние высоты/скролл страницы даже на 1000 строк.
  // Даже при opt-in нужны ≥ VIRTUALIZE_MIN_ROWS строк на странице (на дефолтных
  // 10 — лишний риск без выгоды) и отсутствие `renderAuxRow`: aux-строки — это
  // дополнительные <tr> переменной высоты, которых виртуализатор не знает.
  const virtualEnabled =
    virtualizeRows && !renderAuxRow && pageRows.length >= VIRTUALIZE_MIN_ROWS;
  const rowVirtualizer = useVirtualizer({
    count: pageRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_ESTIMATE_PX,
    getItemKey: (index) => pageRows[index]?.id ?? index,
    overscan: 10,
    enabled: virtualEnabled,
  });

  // Смена страницы/размера страницы — наверх: при включённой виртуализации у
  // контейнера появляется собственный вертикальный скролл, и старый offset
  // показывал бы середину новой страницы.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && el.scrollTop !== 0) el.scrollTop = 0;
  }, [pageIndex, pagination.pageSize]);

  // Видимое окно + две строки-распорки (высота до/после окна): таблица
  // сохраняет table-layout и sticky-ячейки, в отличие от absolute-позиционирования.
  const virtualItems = virtualEnabled ? rowVirtualizer.getVirtualItems() : [];
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom =
    virtualItems.length > 0
      ? rowVirtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
      : 0;
  const visibleColCount = table.getVisibleLeafColumns().length;

  return (
    <div className="card flex flex-col gap-3">
      {/* Toolbar: global search (left) + pagination (right) */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-1">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-surface-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              className="input pl-8 py-1 text-sm w-56"
              placeholder={t('dataTable.search', { defaultValue: 'Search…' })}
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
            {globalFilter && (
              <button
                type="button"
                onClick={() => setGlobalFilter('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-surface-500 hover:text-surface-200"
                aria-label={t('dataTable.clear', { defaultValue: 'Clear' })}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {globalFilter && (
            <span className="text-xs text-surface-500">
              {totalRows} {t('dataTable.matches', { defaultValue: 'matches' })}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 text-xs">
          <span className="text-surface-500">{t('dataTable.rowsPerPage', { defaultValue: 'Rows' })}</span>
          <select
            className="input py-1 text-xs w-auto"
            value={pageSizeRaw === 'all' ? 'all' : String(pageSizeRaw)}
            onChange={(e) => {
              const v = e.target.value;
              setPageSizeRaw(v === 'all' ? 'all' : Number(v));
            }}
          >
            {pageSizeOptions.map((opt) => (
              <option key={String(opt)} value={String(opt)}>
                {opt === 'all' ? t('dataTable.all', { defaultValue: 'All' }) : String(opt)}
              </option>
            ))}
          </select>
          <span className="text-surface-500 tabular-nums">
            {pageIndex + 1} / {Math.max(1, pageCount)} · {totalRows}
          </span>
          <button
            type="button"
            onClick={() => table.previousPage()}
            disabled={!canPrev}
            className="btn-secondary p-1 disabled:opacity-40"
            aria-label={t('dataTable.prev', { defaultValue: 'Previous' })}
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => table.nextPage()}
            disabled={!canNext}
            className="btn-secondary p-1 disabled:opacity-40"
            aria-label={t('dataTable.next', { defaultValue: 'Next' })}
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Пины, не поместившиеся в лимит ширины закреплённой зоны — не молча */}
      {skippedPinnedCount + skippedPinnedCountRight > 0 && (
        <div className="px-1 text-[11px] text-warning" role="status">
          {t('dataTable.pinLimit', {
            n: skippedPinnedCount + skippedPinnedCountRight,
            defaultValue:
              'Not pinned: {{n}} column(s) — the pinned area is capped at 60% of the table width',
          })}
        </div>
      )}

      {/* Table + horizontal auto-scroll (+ вертикальное окно при виртуализации) */}
      <div
        ref={scrollRef}
        className={cn(
          'overflow-x-auto relative',
          // Виртуализатору нужен собственный скролл-элемент с конечной высотой;
          // без max-h окно рендера не сдвигалось бы (скроллится страница, не он).
          // min-h — страховка от height=0 на первом обмере (virtual-core при
          // outerSize=0 отдаёт пустой range и таблица осталась бы пустой).
          // При virtualEnabled=false набор классов ровно как до этой правки.
          virtualEnabled && 'overflow-y-auto max-h-[70vh] min-h-[120px]',
          className,
        )}
      >
        <table ref={tableRef} className="w-full text-sm border-separate border-spacing-0">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => {
                  // isPinned — фактически применённый пин (может быть обрезан
                  // лимитом 60%); pinDesired — состояние пина пользователя,
                  // его и показывает булавка, иначе повторный клик выглядел бы
                  // как «ничего не произошло».
                  const pinSide = h.column.getIsPinned();
                  const isPinned = Boolean(pinSide);
                  const isPinnedLeft = pinSide === 'left';
                  const isPinnedRight = pinSide === 'right';
                  const pinDesired =
                    pinnedLeft.includes(h.column.id) || pinnedRight.includes(h.column.id);
                  const sortDir = h.column.getIsSorted();
                  const canPin = enablePinning && h.column.getCanPin();
                  const pinLabel = pinDesired
                    ? t('dataTable.unpin', { defaultValue: 'Unpin column' })
                    : t('dataTable.pin', { defaultValue: 'Pin column' });
                  return (
                    <th
                      key={h.id}
                      data-col-id={h.column.id}
                      style={{
                        width: h.getSize(),
                        minWidth: h.getSize(),
                        position: isPinned ? 'sticky' : undefined,
                        // Pinned-колонки идут первыми (columnPinning.left order),
                        // поэтому offset = сумма ширин всех предыдущих видимых.
                        left: isPinnedLeft
                          ? sumLeftPinnedOffsets(
                              hg.headers.map((x) => x.column),
                              h.column.id,
                              colWidths,
                            )
                          : undefined,
                        // Правый пин: offset справа = сумма ширин всех правых
                        // пинов правее текущей (columnPinning.right order).
                        right: isPinnedRight
                          ? sumRightPinnedOffsets(
                              hg.headers.map((x) => x.column),
                              h.column.id,
                              pinnedRight,
                              colWidths,
                            )
                          : undefined,
                        zIndex: isPinned ? 3 : 2,
                      }}
                      className={cn(
                        'group/th px-3 py-2 text-xs font-semibold text-surface-400 uppercase tracking-wider select-none',
                        'border-b border-surface-800 bg-surface-900/95 backdrop-blur',
                        h.column.getCanSort() && 'cursor-pointer hover:text-surface-200',
                        isPinnedLeft && 'shadow-[2px_0_0_-1px_rgba(0,0,0,0.4)]',
                        isPinnedRight && 'shadow-[-2px_0_0_-1px_rgba(0,0,0,0.4)]',
                      )}
                      onClick={h.column.getToggleSortingHandler()}
                    >
                      <span className="inline-flex items-center gap-1">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {sortDir === 'asc' && <ChevronUp className="w-3 h-3" />}
                        {sortDir === 'desc' && <ChevronDown className="w-3 h-3" />}
                        {!sortDir && h.column.getCanSort() && (
                          <ChevronsUpDown className="w-3 h-3 opacity-40" />
                        )}
                      </span>
                      {canPin && (
                        <button
                          type="button"
                          onClick={(e) => {
                            // Иначе клик по булавке переключит ещё и сортировку.
                            e.stopPropagation();
                            togglePin(h.column.id);
                          }}
                          aria-pressed={pinDesired}
                          aria-label={pinLabel}
                          title={pinLabel}
                          className={cn(
                            // Ширина 0 в покое: булавка не расширяет колонку.
                            // На широких экранах matching столбцов десятки, и
                            // постоянный +16px на каждый раздувал бы таблицу.
                            'inline-flex items-center align-middle p-0.5 rounded overflow-hidden',
                            'transition-all duration-150 hover:bg-surface-700/50',
                            pinDesired
                              ? cn('w-4 text-primary-300', isPinned ? 'opacity-100' : 'opacity-60')
                              : 'w-0 opacity-0 group-hover/th:w-4 group-hover/th:opacity-100 focus-visible:w-4 focus-visible:opacity-100',
                          )}
                        >
                          <Pin className={cn('w-3 h-3 shrink-0', pinDesired && 'fill-current')} />
                        </button>
                      )}
                      {h.column.getCanFilter() && hasAccessor(h.column) && (
                        <ColumnFacetedFilter
                          column={h.column}
                          loadUniqueValues={loadUniqueValues}
                          allData={data}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {virtualEnabled ? (
              // Виртуализация: окно строк + две строки-распорки. Padding-техника
              // (а не absolute) выбрана намеренно — она сохраняет table-layout,
              // sticky-th/td и горизонтальный скролл без display:block хаков.
              <>
                <SpacerRow height={paddingTop} colSpan={visibleColCount} />
                {virtualItems.map((vi) => {
                  const row = pageRows[vi.index];
                  if (!row) return null;
                  return (
                    <RowFragment
                      key={row.id}
                      row={row}
                      rowIdx={vi.index}
                      onRowClick={onRowClick}
                      rowClassName={rowClassName}
                      renderAuxRow={renderAuxRow}
                      colWidths={colWidths}
                      pinnedRightIds={pinnedRight}
                      measureRef={rowVirtualizer.measureElement}
                      virtualIndex={vi.index}
                    />
                  );
                })}
                <SpacerRow height={paddingBottom} colSpan={visibleColCount} />
              </>
            ) : (
              pageRows.map((row, rowIdx) => (
                <RowFragment
                  key={row.id}
                  row={row}
                  rowIdx={rowIdx}
                  onRowClick={onRowClick}
                  rowClassName={rowClassName}
                  renderAuxRow={renderAuxRow}
                  colWidths={colWidths}
                  pinnedRightIds={pinnedRight}
                />
              ))
            )}
          </tbody>
        </table>

        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-scrim">
            <span className="text-sm text-surface-400">{t('dataTable.loading', { defaultValue: 'Loading…' })}</span>
          </div>
        )}

        {!isLoading && pageRows.length === 0 && (
          <div className="py-10 text-center text-sm text-surface-500">
            {emptyState ?? t('dataTable.noData', { defaultValue: 'No data' })}
          </div>
        )}
      </div>
    </div>
  );
}

// === Internal: a data row + its optional aux banner rows ===

function RowFragment<TData>({
  row,
  rowIdx,
  onRowClick,
  rowClassName,
  renderAuxRow,
  colWidths,
  pinnedRightIds,
  measureRef,
  virtualIndex,
}: {
  row: Row<TData>;
  rowIdx: number;
  onRowClick?: (row: TData) => void;
  rowClassName?: (row: TData) => string;
  renderAuxRow?: (row: TData, index: number) => React.ReactNode;
  /** Обмеренные ширины колонок (для sticky-offset'ов закреплённых ячеек). */
  colWidths?: Record<string, number>;
  /** Идентификаторы правых пинов (для sticky-offset'ов справа). */
  pinnedRightIds?: readonly string[];
  /** ref виртуализатора — только в режиме виртуализации. */
  measureRef?: (el: HTMLTableRowElement | null) => void;
  /** Индекс строки для `measureElement` (`data-index`) — только в виртуализации. */
  virtualIndex?: number;
}) {
  return (
    <>
      <tr
        ref={measureRef}
        data-index={virtualIndex}
        onClick={onRowClick ? () => onRowClick(row.original) : undefined}
        className={cn(
          'border-b border-surface-800/50 transition-colors',
          onRowClick && 'hover:bg-surface-800/30 cursor-pointer',
          rowClassName?.(row.original),
        )}
      >
        {row.getVisibleCells().map((cell) => {
          const pinSide = cell.column.getIsPinned();
          const isPinned = Boolean(pinSide);
          const isPinnedLeft = pinSide === 'left';
          const isPinnedRight = pinSide === 'right';
          return (
            <td
              key={cell.id}
              style={{
                position: isPinned ? 'sticky' : undefined,
                left: isPinnedLeft
                  ? sumLeftPinnedOffsets(
                      row.getVisibleCells().map((c) => c.column),
                      cell.column.id,
                      colWidths,
                    )
                  : undefined,
                right: isPinnedRight
                  ? sumRightPinnedOffsets(
                      row.getVisibleCells().map((c) => c.column),
                      cell.column.id,
                      pinnedRightIds ?? [],
                      colWidths,
                    )
                  : undefined,
                zIndex: isPinned ? 2 : undefined,
                width: cell.column.getSize(),
                minWidth: cell.column.getSize(),
              }}
              className={cn(
                'px-3 py-2 align-top',
                isPinned && 'bg-surface-900/95 backdrop-blur',
                isPinnedLeft && 'shadow-[2px_0_0_-1px_rgba(0,0,0,0.4)]',
                isPinnedRight && 'shadow-[-2px_0_0_-1px_rgba(0,0,0,0.4)]',
              )}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          );
        })}
      </tr>
      {renderAuxRow?.(row.original, rowIdx)}
    </>
  );
}

// === Internal: per-column faceted filter (unique values, lazy-loadable) ===

function ColumnFacetedFilter<TData>({
  column,
  loadUniqueValues,
  allData,
}: {
  column: Column<TData>;
  loadUniqueValues?: DataTableProps<TData>['loadUniqueValues'];
  allData: TData[];
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [lazyValues, setLazyValues] = useState<string[] | null>(null);
  const [lazyOffset, setLazyOffset] = useState(0);
  const [lazyTotal, setLazyTotal] = useState(0);
  const columnId = column.id;

  // Client-derived unique values (cap) when no lazy loader.
  // Uses the column's accessorFn (or accessorKey) so id-only / accessorFn
  // columns also get real faceted values instead of undefined.
  const clientValues = useMemo(() => {
    if (loadUniqueValues) return null;
    const def = column.columnDef as { accessorFn?: (row: TData, i: number) => unknown; accessorKey?: string };
    const accessor = def.accessorFn;
    const accessorKey = def.accessorKey;
    if (!accessor && !accessorKey) return [];
    const set = new Set<string>();
    for (const row of allData) {
      const v = accessor ? accessor(row, 0) : (row as Record<string, unknown>)[accessorKey as string];
      if (v !== null && v !== undefined && v !== '') set.add(String(v));
      if (set.size >= MAX_CLIENT_UNIQUE) break;
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [allData, column, loadUniqueValues]);

  const baseValues = lazyValues ?? clientValues ?? [];
  const filteredValues = baseValues.filter((v) =>
    query ? v.toLowerCase().includes(query.toLowerCase()) : true,
  );

  async function loadMore() {
    if (!loadUniqueValues) return;
    const res = await loadUniqueValues(columnId, lazyOffset, LAZY_CHUNK);
    setLazyValues((prev) => [...(prev ?? []), ...res.values]);
    setLazyOffset((o) => o + LAZY_CHUNK);
    setLazyTotal(res.total);
  }

  function toggle(val: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  }

  function applyFilter() {
    if (selected.size === 0) {
      column.setFilterValue(undefined);
    } else {
      column.setFilterValue((rowVal: unknown) => selected.has(String(rowVal)));
    }
    setOpen(false);
  }

  function clearFilter() {
    setSelected(new Set());
    column.setFilterValue(undefined);
  }

  const active = column.getFilterValue() !== undefined;

  return (
    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
          if (!open && loadUniqueValues && lazyValues === null) loadMore();
        }}
        className={cn(
          'ml-1 inline-flex items-center p-0.5 rounded hover:bg-surface-700/50',
          active ? 'text-primary-300' : 'text-surface-500',
        )}
        aria-label={t('dataTable.filter', { defaultValue: 'Filter' })}
      >
        <Filter className="w-3 h-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 left-0 w-56 max-h-72 overflow-auto card p-2 shadow-xl">
            <input
              type="text"
              className="input py-1 text-xs w-full mb-2"
              placeholder={t('dataTable.searchValues', { defaultValue: 'Search values' })}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            <div className="flex flex-col gap-0.5">
              {filteredValues.map((v) => (
                <label
                  key={v}
                  className="flex items-center gap-2 px-1 py-0.5 text-xs hover:bg-surface-800/50 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(v)}
                    onChange={() => toggle(v)}
                    className="accent-primary-500"
                  />
                  <span className="truncate" title={v}>{v}</span>
                </label>
              ))}
              {filteredValues.length === 0 && (
                <span className="text-xs text-surface-500 px-1 py-1">
                  {t('dataTable.noValues', { defaultValue: 'No values' })}
                </span>
              )}
            </div>
            {loadUniqueValues && lazyValues !== null && lazyValues.length < lazyTotal && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  loadMore();
                }}
                className="mt-2 text-xs text-primary-300 hover:text-primary-200"
              >
                {t('dataTable.loadMore', { defaultValue: 'Load more' })}
              </button>
            )}
            <div className="flex justify-between gap-2 mt-2 pt-2 border-t border-surface-800">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  clearFilter();
                }}
                className="btn-secondary px-2 py-1 text-xs"
              >
                {t('dataTable.clear', { defaultValue: 'Clear' })}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  applyFilter();
                }}
                className="btn-primary px-2 py-1 text-xs"
              >
                {t('dataTable.apply', { defaultValue: 'Apply' })}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// === Internal: строка-распорка виртуализатора (высота до/после окна) ===

function SpacerRow({ height, colSpan }: { height: number; colSpan: number }) {
  if (!(height > 0)) return null;
  return (
    <tr aria-hidden="true">
      <td colSpan={colSpan} style={{ height, padding: 0, border: 0 }} />
    </tr>
  );
}

// === Internal: id колонки из column-def (id → accessorKey) ===

/**
 * react-table выводит id колонки из `accessorKey`, поэтому fallback обязателен:
 * раньше (до 2026-09-23) здесь был только `def.id`, и `pinLeftCount` молча не
 * закреплял ничего у колонок без явного `id`.
 *
 * ВНИМАНИЕ, осознанное визуальное изменение у двух потребителей: теперь
 * `pinLeftCount={1}` реально липит первую колонку в GenerationDataGrid
 * (`accessorKey: 'row_index'`) и DataPreviewModal (`accessorKey: '_idx'`) —
 * узкая колонка «#» слева. Это починка не работавшей функции, не регресс.
 * Остальные потребители задают `id` явно → поведение прежнее.
 */
function columnIdOf<TData>(def: ColumnDef<TData>): string | undefined {
  const d = def as { id?: string; accessorKey?: string };
  return d.id ?? d.accessorKey;
}

// === Internal: сравнения без аллокаций (для идемпотентных setState) ===

function sameIds(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function sameWidths(a: Record<string, number>, b: Record<string, number>): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((k) => Math.abs((a[k] ?? 0) - (b[k] ?? 0)) < 1);
}

// === Internal: закреплённая зона ≤ MAX_PIN_ZONE_RATIO ширины контейнера ===

/**
 * Возвращает префикс `ids`, укладывающийся в лимит ширины закреплённой зоны.
 * Первый пин оставляем всегда: «ничего не закрепилось» на явный запрос
 * пользователя хуже, чем одна широкая закреплённая колонка. Отброшенные пины
 * не теряются — они остаются в состоянии и применяются, как только место есть.
 */
function fitPinnedToWidth(
  ids: readonly string[],
  widthOf: (id: string) => number,
  containerWidth: number,
): { applied: string[]; skipped: number } {
  if (ids.length === 0) return { applied: [], skipped: 0 };
  // До первого обмера контейнера не режем: ширины ещё неизвестны.
  if (!containerWidth) return { applied: [...ids], skipped: 0 };
  const limit = containerWidth * MAX_PIN_ZONE_RATIO;
  const applied: string[] = [];
  let acc = 0;
  for (const id of ids) {
    const w = widthOf(id);
    if (applied.length > 0 && acc + w > limit) break;
    applied.push(id);
    acc += w;
  }
  return { applied, skipped: ids.length - applied.length };
}

/**
 * Правый пин: порядок закрепления = порядок кликов, зона строится от КРАЙНЕГО
 * правого столбца (иначе первый правый пин отъезжал бы от края при доборах).
 * Возвращает суффикс `ids` (правые пины в порядке заявки), укладывающийся в
 * MAX_PIN_ZONE_RATIO ширины контейнера. Лимит зеркален левому: первый
 * запрошенный пин не отбрасывается, отброшенные остаются в состоянии пина
 * и применяются, как только место есть.
 */
function fitPinnedToWidthRight(
  ids: readonly string[],
  widthOf: (id: string) => number,
  containerWidth: number,
): { applied: string[]; skipped: number } {
  if (ids.length === 0) return { applied: [], skipped: 0 };
  // До первого обмера контейнера не режем: ширины ещё неизвестны.
  if (!containerWidth) return { applied: [...ids], skipped: 0 };
  const limit = containerWidth * MAX_PIN_ZONE_RATIO;
  const applied: string[] = [];
  let acc = 0;
  // Идём справа налево, порядок применения сохраняем (unshift).
  for (let i = ids.length - 1; i >= 0; i--) {
    const id = ids[i];
    const w = widthOf(id);
    if (applied.length > 0 && acc + w > limit) break;
    applied.unshift(id);
    acc += w;
  }
  return { applied, skipped: ids.length - applied.length };
}

// === Internal: sum widths of left-pinned columns left of `colId` ===

interface WidthLike {
  id: string;
  getSize: () => number;
}

/** Сумма ширин колонок левее `colId`. Pinned-колонки идут первыми, поэтому
 *  это и есть offset для `position: sticky; left`. Ширины берём обмеренные
 *  (`widths`), с fallback на `getSize()` до первого обмера. */
function sumLeftPinnedOffsets(
  cols: readonly WidthLike[],
  colId: string,
  widths?: Record<string, number>,
): number {
  let offset = 0;
  for (const c of cols) {
    if (c.id === colId) break;
    offset += widths?.[c.id] ?? c.getSize();
  }
  return offset;
}

/** Сумма ширин правых пинов ПРАВЕЕ `colId`: Правые пины идут последними
 *  (columnPinning.right order), поэтому offset для `position: sticky; right`
 *  = сумма ширин всех правых пинов после текущей колонки. */
function sumRightPinnedOffsets(
  cols: readonly WidthLike[],
  colId: string,
  pinnedRightIds: readonly string[],
  widths?: Record<string, number>,
): number {
  let offset = 0;
  let after = false;
  for (const c of cols) {
    if (c.id === colId) {
      after = true;
      continue;
    }
    if (after && pinnedRightIds.includes(c.id)) {
      offset += widths?.[c.id] ?? c.getSize();
    }
  }
  return offset;
}

// === Internal: does a column expose a value (accessorKey / accessorFn)? ===

function hasAccessor<TData>(column: Column<TData>): boolean {
  const def = column.columnDef as { accessorKey?: string; accessorFn?: unknown };
  return Boolean(def.accessorKey ?? def.accessorFn);
}

// === Internal: 120px hover-zone horizontal auto-scroll ===

function useEdgeAutoScroll(ref: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ZONE = 120;
    let raf = 0;
    let active = false;
    let dir = 0;
    let speed = 0;

    const loop = () => {
      if (active && el) el.scrollLeft += dir * speed;
      raf = requestAnimationFrame(loop);
    };

    const onMove = (e: MouseEvent) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const fromLeft = x;
      const fromRight = rect.width - x;
      if (fromLeft < ZONE && el.scrollLeft > 0) {
        active = true;
        dir = -1;
        speed = Math.max(1, (ZONE - fromLeft) / 10);
      } else if (fromRight < ZONE && el.scrollLeft < el.scrollWidth - el.clientWidth) {
        active = true;
        dir = 1;
        speed = Math.max(1, (ZONE - fromRight) / 10);
      } else {
        active = false;
        speed = 0;
      }
    };
    const onLeave = () => {
      active = false;
      speed = 0;
    };

    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    raf = requestAnimationFrame(loop);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(raf);
    };
  }, [ref]);
}